import React, { useState, useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';
import AgentActivityFeed from '../agents/AgentActivityFeed.js';
import StructuredRenderer from '../structured/StructuredRenderer.js';

const getStyles = (theme) => ({
  message: {
    marginBottom: theme.spacing.md,
    display: 'flex',
    alignItems: 'flex-start',
    lineHeight: '1.4',
  },
  messageUser: {
    flexDirection: 'row-reverse',
  },
  messageBot: {
    flexDirection: 'row',
  },
  messageBubble: {
    maxWidth: '70%',
    padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.background.sidebar,
    fontSize: theme.fontSize.md,
    lineHeight: '1.4',
    boxShadow: theme.shadows.small,
  },
  messageBubbleUser: {
    backgroundColor: theme.colors.background.sidebar,
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text.primary,
    marginLeft: theme.spacing.md,
  },
  messageBubbleBot: {
    backgroundColor: theme.colors.background.sidebar,
    color: theme.colors.text.primary,
    border: `0px solid ${theme.colors.border}`,
    marginRight: theme.spacing.md,
  },
  messageTime: {
    fontSize: theme.fontSize.xs,
    color: theme.colors.text.muted,
    marginTop: theme.spacing.xs,
  },
  markdownContent: {
    fontSize: theme.fontSize.md,
    lineHeight: '1.4',
  },
  streamingIndicator: {
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.text.secondary,
    fontSize: theme.fontSize.md,
    fontStyle: 'italic',
  },
  streamingDots: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing.sm,
  },
  streamingDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    backgroundColor: theme.colors.text.secondary,
    borderRadius: '50%',
    marginRight: theme.spacing.xs,
    animation: 'pulse 1.4s infinite ease-in-out',
  },
  errorText: {
    marginLeft: theme.spacing.sm,
    fontSize: theme.fontSize.xs,
    color: theme.colors.danger,
    fontStyle: 'italic',
  },
  groundingSources: {
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: 'rgba(100, 181, 246, 0.08)',
    borderRadius: theme.borderRadius.sm,
    borderLeft: `3px solid ${theme.colors.primary}`,
  },
  groundingHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    marginBottom: theme.spacing.xs,
  },
  groundingTitle: {
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  groundingIcon: {
    fontSize: '14px',
  },
  groundingCount: {
    fontSize: theme.fontSize.xs,
    color: theme.colors.text.muted,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: '2px 6px',
    borderRadius: '10px',
  },
  groundingList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    marginTop: theme.spacing.xs,
  },
  groundingItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.xs} 0`,
    borderBottom: `1px solid rgba(255, 255, 255, 0.05)`,
  },
  groundingItemLast: {
    borderBottom: 'none',
  },
  groundingItemIcon: {
    fontSize: '12px',
    marginTop: '2px',
    opacity: 0.7,
  },
  groundingItemContent: {
    flex: 1,
  },
  groundingItemTitle: {
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.medium,
    color: theme.colors.text.primary,
    marginBottom: '2px',
  },
  groundingItemUri: {
    fontSize: '10px',
    color: theme.colors.text.muted,
    wordBreak: 'break-all',
    textDecoration: 'none',
  },
  groundingItemText: {
    fontSize: theme.fontSize.xs,
    color: theme.colors.text.secondary,
    marginTop: '4px',
    lineHeight: '1.4',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    color: theme.colors.primary,
    fontSize: theme.fontSize.xs,
    cursor: 'pointer',
    padding: 0,
    marginTop: theme.spacing.xs,
    textDecoration: 'underline',
  },
});

const renderMarkdown = (theme, content) => {
  if (!content) return '';

  // Handle non-string content (structured data objects)
  if (typeof content !== 'string') {
    if (typeof content === 'object') {
      if (content.text) return renderMarkdown(theme, content.text);
      if (content.message) return renderMarkdown(theme, content.message);
      if (content.response) return renderMarkdown(theme, content.response);
      if (content.content && typeof content.content === 'string') return renderMarkdown(theme, content.content);

      // Fallback: stringify the object in a readable way
      try {
        return `<pre style="background: ${theme.colors.background.modal}; padding: ${theme.spacing.sm}; border-radius: ${theme.borderRadius.sm}; overflow-x: auto; margin: ${theme.spacing.sm} 0; white-space: pre-wrap;"><code>${JSON.stringify(content, null, 2)}</code></pre>`;
      } catch (e) {
        return '[Unable to display structured content]';
      }
    }
    return String(content);
  }

  // Convert markdown to HTML-like structure
  let html = content
    // Headers
    .replace(
      /^### (.*$)/gim,
      `<h3 style="margin: ${theme.spacing.sm} 0 ${theme.spacing.xs} 0; font-size: ${theme.fontSize.xl}; font-weight: ${theme.fontWeight.semibold};">$1</h3>`,
    )
    .replace(
      /^## (.*$)/gim,
      `<h2 style="margin: ${theme.spacing.sm} 0 ${theme.spacing.xs} 0; font-size: ${theme.fontSize.xxl}; font-weight: ${theme.fontWeight.semibold};">$1</h2>`,
    )
    .replace(
      /^# (.*$)/gim,
      `<h1 style="margin: ${theme.spacing.md} 0 ${theme.spacing.sm} 0; font-size: ${theme.fontSize.xxxl}; font-weight: ${theme.fontWeight.semibold};">$1</h1>`,
    )
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Code blocks
    .replace(
      /```([\s\S]*?)```/g,
      `<pre style="background: ${theme.colors.background.modal}; padding: ${theme.spacing.sm}; border-radius: ${theme.borderRadius.sm}; overflow-x: auto; margin: ${theme.spacing.sm} 0;"><code>$1</code></pre>`,
    )
    // Inline code
    .replace(
      /`([^`]+)`/g,
      `<code style="background: ${theme.colors.background.modal}; padding: ${theme.spacing.xs} ${theme.spacing.xs}; border-radius: ${theme.borderRadius.sm};">$1</code>`,
    )
    // Links
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      `<a href="$2" target="_blank" style="color: ${theme.colors.background_hover.button}; text-decoration: none;">$1</a>`,
    )
    // Line breaks
    .replace(/\n/g, '<br>');

  return html;
};

const GroundingSources = ({ groundingMetadata }) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const [isExpanded, setIsExpanded] = useState(false);

  if (!groundingMetadata) return null;

  const { groundingChunks, retrievalQueries } = groundingMetadata;

  // Only show if there are grounding chunks
  if (!groundingChunks || groundingChunks.length === 0) return null;

  // Process grounding chunks to extract relevant info
  const sources = groundingChunks
    .map((chunk, index) => {
      // Handle different chunk types
      if (chunk.retrievedContext) {
        return {
          type: 'rag',
          title: chunk.retrievedContext.title || `Source ${index + 1}`,
          uri: chunk.retrievedContext.uri || null,
          text: chunk.retrievedContext.text || null,
        };
      }
      if (chunk.web) {
        return {
          type: 'web',
          title: chunk.web.title || chunk.web.domain || 'Web Source',
          uri: chunk.web.uri || null,
          domain: chunk.web.domain || null,
        };
      }
      return null;
    })
    .filter(Boolean);

  if (sources.length === 0) return null;

  const displaySources = isExpanded ? sources : sources.slice(0, 3);

  return (
    <div style={styles.groundingSources}>
      <div style={styles.groundingHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <div style={styles.groundingTitle}>
          <span style={styles.groundingIcon}>📚</span>
          <span>Sources</span>
        </div>
        <span style={styles.groundingCount}>
          {sources.length} {sources.length === 1 ? 'source' : 'sources'}
        </span>
      </div>

      {isExpanded && retrievalQueries && retrievalQueries.length > 0 && (
        <div style={{ fontSize: '10px', color: theme.colors.text.muted, marginBottom: theme.spacing.xs }}>
          Query: {retrievalQueries.join(', ')}
        </div>
      )}

      <ul style={styles.groundingList}>
        {displaySources.map((source, index) => (
          <li
            key={index}
            style={{
              ...styles.groundingItem,
              ...(index === displaySources.length - 1 ? styles.groundingItemLast : {}),
            }}
          >
            <span style={styles.groundingItemIcon}>{source.type === 'rag' ? '📄' : '🌐'}</span>
            <div style={styles.groundingItemContent}>
              <div style={styles.groundingItemTitle}>{source.title}</div>
              {source.uri && (
                <a href={source.uri} target="_blank" rel="noopener noreferrer" style={styles.groundingItemUri}>
                  {source.uri}
                </a>
              )}
              {source.text && <div style={styles.groundingItemText}>{source.text}</div>}
            </div>
          </li>
        ))}
      </ul>

      {sources.length > 3 && !isExpanded && (
        <button style={styles.expandButton} onClick={() => setIsExpanded(true)}>
          Show {sources.length - 3} more sources
        </button>
      )}

      {isExpanded && sources.length > 3 && (
        <button style={styles.expandButton} onClick={() => setIsExpanded(false)}>
          Show less
        </button>
      )}
    </div>
  );
};

const ChatMessage = ({ message, formatTime }) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const isUser = message.type === 'user';
  const isBot = message.type === 'bot';
  const isSystem = message.type === 'system';

  const messageStyle = {
    ...styles.message,
    ...(isUser ? styles.messageUser : styles.messageBot),
  };

  const bubbleStyle = {
    ...styles.messageBubble,
    ...(isUser ? styles.messageBubbleUser : styles.messageBubbleBot),
  };

  const renderStreamingIndicator = () => (
    <div style={styles.streamingIndicator}>
      <div style={styles.streamingDots}>
        <div style={styles.streamingDot}></div>
        <div style={styles.streamingDot}></div>
        <div style={styles.streamingDot}></div>
      </div>
      <span>AI is typing...</span>
    </div>
  );

  const renderMessageContent = () => {
    if (message.isStreaming) {
      const hasContent = message.content && message.content.trim().length > 0;
      const hasActivitySteps = message.activitySteps && message.activitySteps.length > 0;
      const hasActiveSteps = hasActivitySteps && message.activitySteps.some((s) => s.status === 'active');

      if (hasActiveSteps && !hasContent) {
        return <AgentActivityFeed steps={message.activitySteps} />;
      }

      if (hasContent) {
        const htmlContent = renderMarkdown(theme, message.content);
        return (
          <>
            {hasActivitySteps && <AgentActivityFeed steps={message.activitySteps} compact />}
            <div style={styles.markdownContent} dangerouslySetInnerHTML={{ __html: htmlContent }} />
            {renderStreamingIndicator()}
          </>
        );
      }

      return <AgentActivityFeed steps={[]} />;
    }

    const hasCompletedSteps =
      message.activitySteps &&
      message.activitySteps.length > 0 &&
      message.activitySteps.some((s) => s.type !== 'thinking');

    if (message.content) {
      const isStructuredObject = typeof message.content === 'object' && message.content !== null;
      const hasMarkdownTable = typeof message.content === 'string' && message.content.includes('|---');
      const useStructuredRenderer = isStructuredObject || hasMarkdownTable;

      return (
        <>
          {hasCompletedSteps && <AgentActivityFeed steps={message.activitySteps} compact />}
          {useStructuredRenderer ? (
            <div style={styles.markdownContent}>
              <StructuredRenderer response={message.content} />
            </div>
          ) : (
            <div
              style={styles.markdownContent}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(theme, message.content) }}
            />
          )}
        </>
      );
    }

    return <div>No content</div>;
  };

  return (
    <div style={messageStyle}>
      <div style={bubbleStyle}>
        {renderMessageContent()}
        {/* Show grounding sources for bot messages with RAG-enhanced responses */}
        {isBot && message.groundingMetadata && <GroundingSources groundingMetadata={message.groundingMetadata} />}
        {message.timestamp && formatTime && <div style={styles.messageTime}>{formatTime(message.timestamp)}</div>}
      </div>
    </div>
  );
};

export default ChatMessage;
